import React, { useState, useEffect, useContext, useRef } from "react";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import { FiEye, FiEdit2, FiTrash2, FiPlus, FiX } from "react-icons/fi";
import AuthContext from "../../contexts/AuthContext";
import AdminLayout from "../../layouts";
import "./kmw-employees.css";

const EMPTY_FILTERS = { name: "", email: "", dob: "" };
const EMPTY_FORM = { name: "", email: "", dob: "" };

// Backend returns dob as a Jackson-serialized array [yyyy, mm, dd, hh, mm]
// (LocalDateTime without a date module) rather than a string — rendering
// that array directly in JSX concatenates the numbers with no separator
// (e.g. "20003200"), since React renders array children without a join.
// Normalizes it to the "YYYY-MM-DD" string every <input type="date"> and
// display spot in this page expects.
const formatDob = (dob) => {
  if (Array.isArray(dob) && dob.length >= 3) {
    const [year, month, day] = dob;
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }
  if (typeof dob === "string" && dob) {
    return dob.slice(0, 10);
  }
  return "";
};

// NOTE: request/response field names below (`name`, `email`, `dob`,
// `pkEmpDetailId`) follow the pattern used by the other list pages and the
// `/employee/update/{pkEmpDetailId}` / `/employee/delete/{pkEmpDetailId}` URL
// shape — verify against the real backend response once available.
const KMWEmployees = () => {
  const { token, role, triggerSessionExpired } = useContext(AuthContext);

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [activeSearch, setActiveSearch] = useState({});

  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // formModal: null | { mode: "add" } | { mode: "edit", employee }
  const [formModal, setFormModal] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const abortControllerRef = useRef(null);

  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, activeSearch]);

  useEffect(() => {
    return () => abortControllerRef.current?.abort();
  }, []);

  // Lets the open modal (view, form, or delete-confirm) be dismissed with Escape.
  useEffect(() => {
    if (!selectedEmployee && !formModal && !deleteTarget) return;
    const onKeyDown = (e) => {
      if (e.key !== "Escape") return;
      setSelectedEmployee(null);
      setFormModal(null);
      if (!deleting) setDeleteTarget(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedEmployee, formModal, deleteTarget, deleting]);

  const fetchEmployees = async () => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/employee/search`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            name: activeSearch.name || "",
            email: activeSearch.email || "",
            dob: activeSearch.dob || "",
            pageNo: page,
            pageSize,
          }),
          signal: controller.signal,
        }
      );

      if (response.status === 401 || response.status === 403) {
        triggerSessionExpired();
        return;
      }

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.message || `Request failed (${response.status})`);
      }

      const status = (data?.status || "").toLowerCase();
      if (status && status !== "successful" && status !== "success") {
        toast.error(data?.message || "Failed to fetch employees.");
        setEmployees([]);
        setTotalRows(0);
        return;
      }

      setEmployees(Array.isArray(data?.data) ? data.data : []);
      setTotalRows(data?.totalResult || 0);
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error("Error fetching employees:", error);
      toast.error(error.message || "Failed to fetch employees.");
      setEmployees([]);
      setTotalRows(0);
    } finally {
      if (abortControllerRef.current === controller) {
        setLoading(false);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = () => {
    setPage(1);
    setActiveSearch(filters);
  };

  const handleClear = () => {
    setFilters(EMPTY_FILTERS);
    setActiveSearch(EMPTY_FILTERS);
    setPage(1);
  };

  const openAddModal = () => {
    setFormData(EMPTY_FORM);
    setFormErrors({});
    setFormModal({ mode: "add" });
  };

  const openEditModal = (row) => {
    setFormData({
      name: row?.name || "",
      email: row?.email || "",
      dob: formatDob(row?.dob),
    });
    setFormErrors({});
    setFormModal({ mode: "edit", employee: row });
  };

  const closeFormModal = () => {
    setFormModal(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "Name is required.";
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required.";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Invalid email format.";
    }

    if (!formData.dob) {
      errors.dob = "Date of birth is required.";
    } else if (new Date(formData.dob) > new Date()) {
      errors.dob = "Date of birth cannot be in the future.";
    }

    return errors;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const isEdit = formModal?.mode === "edit";
    const pkEmpDetailId = formModal?.employee?.pkEmpDetailId;
    const url = isEdit
      ? `${process.env.REACT_APP_API_BASE_URL}/employee/update/${pkEmpDetailId}`
      : `${process.env.REACT_APP_API_BASE_URL}/employee/add`;

    setSubmitting(true);
    try {
      const response = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          dob: formData.dob,
        }),
      });

      if (response.status === 401 || response.status === 403) {
        triggerSessionExpired();
        return;
      }

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(
          data?.message || `Request failed (${response.status})`
        );
      }

      toast.success(isEdit ? "Employee updated successfully!" : "Employee added successfully!");
      closeFormModal();
      fetchEmployees();
    } catch (error) {
      console.error("Error saving employee:", error);
      toast.error(error.message || "Failed to save employee. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const requestDelete = (row) => {
    setDeleteTarget(row);
  };

  const cancelDelete = () => {
    if (deleting) return; // don't let the backdrop/Escape close it mid-request
    setDeleteTarget(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    setDeleting(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/employee/delete/${deleteTarget?.pkEmpDetailId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 401 || response.status === 403) {
        triggerSessionExpired();
        return;
      }

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.message || `Request failed (${response.status})`);
      }

      toast.success("Employee deleted successfully!");
      setDeleteTarget(null);
      fetchEmployees();
    } catch (error) {
      console.error("Error deleting employee:", error);
      toast.error(error.message || "Failed to delete employee. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    { name: "Name", selector: (row) => row?.name || "-", sortable: true },
    { name: "Email", selector: (row) => row?.email || "-", sortable: true },
    { name: "DOB", selector: (row) => formatDob(row?.dob) || "-", sortable: true },
    {
      name: "Action",
      cell: (row) => (
        <div className="action-btn-group">
          <button
            type="button"
            className="action-view-btn"
            onClick={() => setSelectedEmployee(row)}
            aria-label="View details"
          >
            <FiEye size={16} />
          </button>
          <button
            type="button"
            className="action-edit-btn"
            onClick={() => openEditModal(row)}
            aria-label="Edit employee"
          >
            <FiEdit2 size={16} />
          </button>
          <button
            type="button"
            className="action-delete-btn"
            onClick={() => requestDelete(row)}
            aria-label="Delete employee"
          >
            <FiTrash2 size={16} />
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "150px",
    },
  ];

  // Themes the table to match the panel's premium redesign (brand-gradient
  // header row, softer hover tint) without touching react-data-table-component's markup.
  const tableCustomStyles = {
    headRow: {
      style: {
        background: "var(--admin-bg)",
        minHeight: "52px",
        borderBottom: "1px solid var(--admin-border)",
      },
    },
    headCells: {
      style: {
        color: "var(--admin-text-primary)",
        fontSize: "13px",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.03em",
      },
    },
    rows: {
      style: {
        minHeight: "54px",
        fontSize: "14px",
        color: "#29282b",
      },
      highlightOnHoverStyle: {
        backgroundColor: "rgba(0, 180, 255, 0.08)",
        borderBottomColor: "#e4e7ee",
        outline: "none",
      },
    },
    pagination: {
      style: {
        borderTop: "1px solid #eef0f5",
      },
    },
  };

  // This page is only ever mounted behind SuperAdminRoute, but keep the check
  // here too so nothing renders in the unlikely case it's reused elsewhere.
  if (role !== "Super Admin") return null;

  return (
    <AdminLayout>
      <div className="kmw-employees">
        <div className="page-banner">
          <div className="page-banner-text">
            <span className="page-banner-tag">Team</span>
            <h1 className="page-banner-title">KMW Employees</h1>
          </div>
          <div className="page-banner-badge">
            <span className="page-banner-badge-label">Total</span>
            <span className="page-banner-badge-value">{totalRows}</span>
          </div>
        </div>

        {/* --- SEARCH FILTER SECTION --- */}
        <div className="search-filter-card">
          <div className="search-grid">
            <div className="filter-group">
              <label>Name:</label>
              <input
                type="text"
                name="name"
                placeholder="Enter Name"
                value={filters.name}
                onChange={handleInputChange}
              />
            </div>
            <div className="filter-group">
              <label>Email:</label>
              <input
                type="email"
                name="email"
                placeholder="Enter Email"
                value={filters.email}
                onChange={handleInputChange}
              />
            </div>
            <div className="filter-group">
              <label>Date of Birth:</label>
              <input
                type="date"
                name="dob"
                value={filters.dob}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="search-actions">
            <button className="btn-search" onClick={handleSearch}>
              Search
            </button>
            <button className="btn-clear" onClick={handleClear}>
              Clear
            </button>
            <button className="btn-add" onClick={openAddModal}>
              <FiPlus size={16} />
              Add Employee
            </button>
          </div>
        </div>

        {/* --- DATA TABLE SECTION --- */}
        <div className="kmw-employees-body">
          <DataTable
            keyField="pkEmpDetailId"
            columns={columns}
            data={employees}
            progressPending={loading}
            pagination
            paginationServer
            paginationTotalRows={totalRows}
            paginationPerPage={pageSize}
            onChangePage={(p) => setPage(p)}
            onChangeRowsPerPage={(ps, p) => {
              setPageSize(ps);
              setPage(p);
            }}
            highlightOnHover
            customStyles={tableCustomStyles}
          />
        </div>
      </div>

      {/* --- VIEW MODAL --- */}
      {selectedEmployee && (
        <div
          className="detail-modal-overlay"
          onClick={() => setSelectedEmployee(null)}
        >
          <div className="detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="detail-modal-header">
              <h3>Employee Details</h3>
              <button
                type="button"
                className="detail-modal-close"
                onClick={() => setSelectedEmployee(null)}
                aria-label="Close"
              >
                <FiX size={20} />
              </button>
            </div>
            <div className="detail-modal-body">
              <div className="detail-row">
                <span className="detail-label">Name</span>
                <span className="detail-value">{selectedEmployee?.name || "-"}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Email</span>
                <span className="detail-value">{selectedEmployee?.email || "-"}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Date of Birth</span>
                <span className="detail-value">{formatDob(selectedEmployee?.dob) || "-"}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- ADD / EDIT FORM MODAL --- */}
      {formModal && (
        <div className="detail-modal-overlay" onClick={closeFormModal}>
          <div className="detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="detail-modal-header">
              <h3>{formModal.mode === "edit" ? "Edit Employee" : "Add Employee"}</h3>
              <button
                type="button"
                className="detail-modal-close"
                onClick={closeFormModal}
                aria-label="Close"
              >
                <FiX size={20} />
              </button>
            </div>
            <form className="employee-form-body" onSubmit={handleFormSubmit} noValidate>
              <div className="employee-form-group">
                <label>
                  <span className="text-danger">* </span>Name
                </label>
                <input
                  type="text"
                  name="name"
                  placeholder="Enter Name"
                  value={formData.name}
                  onChange={handleFormChange}
                  className={formErrors.name ? "is-invalid" : ""}
                />
                {formErrors.name && (
                  <div className="employee-form-error">{formErrors.name}</div>
                )}
              </div>

              <div className="employee-form-group">
                <label>
                  <span className="text-danger">* </span>Email
                </label>
                <input
                  type="email"
                  name="email"
                  placeholder="Enter Email"
                  value={formData.email}
                  onChange={handleFormChange}
                  className={formErrors.email ? "is-invalid" : ""}
                />
                {formErrors.email && (
                  <div className="employee-form-error">{formErrors.email}</div>
                )}
              </div>

              <div className="employee-form-group">
                <label>
                  <span className="text-danger">* </span>Date of Birth
                </label>
                <input
                  type="date"
                  name="dob"
                  value={formData.dob}
                  onChange={handleFormChange}
                  className={formErrors.dob ? "is-invalid" : ""}
                />
                {formErrors.dob && (
                  <div className="employee-form-error">{formErrors.dob}</div>
                )}
              </div>

              <div className="employee-form-actions">
                <button
                  type="button"
                  className="btn-clear"
                  onClick={closeFormModal}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-search" disabled={submitting}>
                  {submitting ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- DELETE CONFIRM MODAL --- */}
      {deleteTarget && (
        <div className="detail-modal-overlay" onClick={cancelDelete}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-modal-top">
              <div className="confirm-modal-icon">
                <FiTrash2 size={22} />
              </div>
              <button
                type="button"
                className="confirm-modal-close"
                onClick={cancelDelete}
                aria-label="Close"
                disabled={deleting}
              >
                <FiX size={18} />
              </button>
            </div>

            <h3 className="confirm-modal-title">Delete Employee</h3>
            <p className="confirm-modal-subtitle">
              Confirm employee removal from the system records
            </p>

            <p className="confirm-modal-question">
              Are you sure you want to delete this employee? This cannot be undone.
            </p>

            <div className="confirm-target-box">
              <span className="confirm-target-label">Target Employee</span>
              <span className="confirm-target-value">{deleteTarget?.name || "-"}</span>
            </div>

            <div className="confirm-modal-actions">
              <button
                type="button"
                className="btn-clear"
                onClick={cancelDelete}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-delete-confirm"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default KMWEmployees;
